
import { ConfirmationModal } from "./confirmation-modal";
import React, { useState } from "react";
import * as StoreReview from 'expo-store-review';
import { isDeviceVirtual } from "../../utils/virtualDevice";
import * as Device from "expo-device";
import { Platform } from 'react-native';
import { DomusoApiService } from "../../api/DomusoApiService";
import { MockService } from "../../api/MockService";
import { translate } from "../../i18n/translate";
import { Portal, useTheme } from "react-native-paper";
import { ThemeType } from "../../theme/theme";
import { StyleSheet, Linking } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/reducers";
import { useLocalMock } from "../../enums/check";
import { getDifferenceInDays } from "../../utils/date";
import { AppReviewState } from "../../enums";
import { updateAppReviewData } from "../../redux/actions/actionCreators";
import moment from "moment";

export const AppReviewModal = () => {    
    const theme = useTheme() as ThemeType;
    const styles = getStyles(theme);
    const userInfo = useSelector((state: RootState) => state.auth.userInfo);
    const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
    const logger = useSelector((state: RootState) => state.logger);
    
    let dateUpdated = useSelector((state: RootState) => state.appReview.dateUpdated);
    let dateCreated = useSelector((state: RootState) => state.appReview.dateCreated);
    const nextRunDays = useSelector((state: RootState) => state.appReview.nextRunDays);
    const username = userInfo?.username;
    const dispatch = useDispatch();
    const domusoApiService = useLocalMock
    ? new MockService(cognitoUser, dispatch)
    : new DomusoApiService(cognitoUser, dispatch);  
  
    const [displayAppRevieModal, setDisplayAppReviewModal] = useState(shouldDisplayAppReviewModal());
    const [userDislikesApp, setUserDislikesApp] = useState(false);
    
    function shouldDisplayAppReviewModal() {
        try {
            //Do not display mobile if on web
            if (Platform.OS === 'web') {
                return false;
            }
            //Do not display modal if we don't have all the required information
            if (!nextRunDays || !username || !domusoApiService || nextRunDays <= 0) {
                logger.info(`Missing information will not display custom app review modal. nextRunDays: ${nextRunDays} ${!username ? "missing username" :""}`);
            return false; 
            }
            if (!dateCreated) {
                //It means user was never requested for app review.
                return true;
            } else {
                const lastDateUpdated =  moment(dateUpdated, "YYYY-MM-DD");
                return getDifferenceInDays(lastDateUpdated, moment()) >= Number(nextRunDays);                 
            }
        } catch(error) {
            logger.info(`Error while attempting to display the custom app review modal. Info: dateCreated: ${dateCreated} dateUpdated: ${dateUpdated} nextRunDays: ${nextRunDays}`);
            logger.error(error);
            return false;
        } 
    }

    //Will attempt to display the review dialog using StoreReview.requestReview which relies on IOS/Android native functionality.
    //IOS limits the number of requests to a maximum of three times within a 365-day period.
    //Android enforces a time-bound quota on how often a user can be shown the review dialog, this quota is not disclosed.
    //In both scenarios where the user already rate the app or decided to cancel it is possible that the review dialog will not show until time-bound quotas get ressetted.
    function requestNativeOsReview() {
        //This check will not be required after EXPO SDK gets updated to anything above version 43
        if (isDeviceVirtual() && Device?.osName === "Android") {
            //https://github.com/expo/expo/issues/11784
            logger.info("Will not display review dialog in Android emulator due to a known bug in EXPO SKD <= 43");
            return;
        }

        const promptUserForReview = async () => { 
            logger.info("Attempting to display the review dialog.");
            //Determines if the platform supports StoreReview.requestReview().
            //Supported versions are: iOS 10.3+, Android 5.0+, web is not supported.
            const platformSupportsReview =  await StoreReview.isAvailableAsync();
            if(platformSupportsReview){
            await StoreReview.requestReview().then(function(response: any) {               
                logger.info("Requested app review, its up to the platform to decide if review dialog should be displayed: ", {response});
            });
            }else {
                logger.info("Unsupported IOS/Android version, will not display review dialog.");
            }
        }        
        promptUserForReview().catch((error) => {            
            logger.info(`Error while attempting to display the native OS review dialog. Device: ${Device?.osName}`);
            logger.error(error);            
        });
        
    }

    async function handleDislike() {
        setDisplayAppReviewModal(false);
        setUserDislikesApp(true);
        await recordAppReview(AppReviewState.DISLIKE); 
    }

   async function handleLike() {
        setDisplayAppReviewModal(false);
        await recordAppReview(AppReviewState.LIKE);
        requestNativeOsReview();
    }

    function handleConfirm() {
        setUserDislikesApp(false);
    }

   async function recordAppReview(userReview: AppReviewState | null) {
        try {            
            const response = await domusoApiService.submitAppReviewData({userReview});
            dispatch(updateAppReviewData({userReview: response.userReview, dateCreated: response.dateCreated, dateUpdated: response.dateUpdated}));            
        }catch(error){
            logger.info("Failed submitAppReviewData POST request");
            logger.error(error);
            setUserDislikesApp(false);
            setDisplayAppReviewModal(false);
        }
    }

    async function handleDimiss() {
        //User can close the modal by clicking outside in which case we need to update the 
        // review so we don't display the modal until nextRunDays.
        await recordAppReview(null)
    }

    return (
        <Portal>
            <ConfirmationModal
                visible={displayAppRevieModal}
                setVisible={setDisplayAppReviewModal}
                isLoading={false}
                confirmCallback={async () => await handleLike()}
                denyCallback={async () => await handleDislike()}
                onDismissCallback={async () => await handleDimiss()}
                bodyText={translate("appReviewModal.message")  as string}
                bodyTextStyle={styles.bodyText}
                buttonConfirmText={translate("appReviewModal.confirmButton") as string}
                buttonCancelText ={translate("appReviewModal.cancelButton") as string}
                buttonConfirmStyle= {styles.buttonConfirm}
                buttonCancelStyle = {styles.buttonCancel}    
            />
            <ConfirmationModal
                visible={userDislikesApp}
                setVisible={setUserDislikesApp}
                isLoading={false}
                confirmCallback={handleConfirm}
                bodyText={translate("appReviewModal.dislikeMessage")  as string}
                buttonConfirmText={translate("appReviewModal.dislikeConfirmButton") as string}
                buttonConfirmStyle= {styles.buttonConfirm}
                bodyTextClickCallback ={() => Linking.openURL('mailto:product@domuso.com?subject=Domuso resident app Feedback')}
                hideCancelButton={true}
                wordsToHighlight= {["product@domuso.com"]}
            />
     </Portal>
    );
}

const getStyles = (theme: ThemeType) => {
    const { colors, spacing, fontSize, iconSize } = theme;
    return StyleSheet.create({
      bodyText: {
        textAlign: "center",
        fontWeight: "bold",        
      },
      buttonConfirm: {
        width:130
      },
      buttonCancel: {     
        backgroundColor: colors.white
      }
    });
  }